/**
 * Mensajes funcionales controlados por Web. La API entrega error_code y la
 * interfaz evita mostrar razones internas, URLs o errores completos.
 */
const API_ERROR_MESSAGES: {[errorCode: string]: string} = {
  DETENTION_ACCESS_DENIED:
    'No tienes permisos para editar esta detención.',
  DETENTION_NOT_FOUND:
    'La detención ya no está disponible. Actualiza el listado.',
  DETENTION_VALIDATION_FAILED:
    'Revisa los datos de la detención antes de guardar.',
  DETENTION_SAVE_FAILED:
    'No fue posible guardar los cambios de la detención.',
  DETENTION_REBUILD_FAILED:
    'La detención fue actualizada.',
  DETENTION_REBUILD_FORBIDDEN:
    'La detención fue actualizada.',
  DETENTION_REPORT_REGENERATION_FAILED:
    'La detención fue actualizada.',
  COMPANY_ACCESS_DENIED:
    'No tienes permisos para registrar producción en la empresa seleccionada.',
  PRODUCTION_REBUILD_FORBIDDEN:
    'La producción fue guardada.',
  PRODUCTION_REBUILD_FAILED:
    'La producción fue guardada.',
  DUPLICATE_WORK_ORDER_PRODUCT:
    'No se puede agregar el mismo producto más de una vez a la orden de trabajo.',
  PRODUCT_COMPANY_MISMATCH:
    'Uno de los productos no pertenece a la empresa seleccionada.',
  WORK_ORDER_PRODUCT_IN_USE:
    'No se puede retirar el producto porque está asociado a un lote o registro de producción.',
  WORK_ORDER_IN_USE:
    'No se puede modificar la orden porque uno de sus productos ya está en uso.',
  WORK_ORDER_PRODUCTS_REQUIRED:
    'La orden de trabajo debe incluir al menos un producto.',
  INVALID_PLANNED_UNITS:
    'La cantidad planificada debe ser igual o superior a cero.',
  LOT_PRODUCT_NOT_IN_WORK_ORDER:
    'El producto del lote debe estar incluido en la orden de trabajo.',
  REPORT_REGENERATION_FAILED:
    'La producción fue guardada.',
  REPORT_RESEND_FAILED:
    'No fue posible reenviar el reporte por correo.'
};

function getResponseBody(error: any): any {
  return error && error.error !== undefined ? error.error : (error || {});
}

export function getApiErrorCode(error: any): string | null {
  const body = getResponseBody(error);
  const detail = body && typeof body.error === 'object' ? body.error : {};

  return detail.error_code || body.error_code ||
    (body.data && body.data.error_code) || null;
}

export function getApiRequestId(error: any): string | null {
  const body = getResponseBody(error);
  const detail = body && typeof body.error === 'object' ? body.error : {};

  return body.request_id || detail.request_id ||
    (body.data && body.data.request_id) || null;
}

export function isProductionRebuildPartialError(error: any): boolean {
  const errorCode = getApiErrorCode(error);
  if (errorCode === 'PRODUCTION_REBUILD_FORBIDDEN' ||
      errorCode === 'PRODUCTION_REBUILD_FAILED' ||
      errorCode === 'REPORT_REGENERATION_FAILED') {
    return true;
  }

  // Compatibilidad temporal con la respuesta previa a error_code.
  const body = getResponseBody(error);
  const detail = body && typeof body.error === 'object' ? body.error : {};
  const httpCode = error && error.status || detail.code || body.code;
  const remoteStatus = body.remote_status || detail.remote_status;

  return Number(httpCode) === 502 && Number(remoteStatus) === 403;
}

/** Reconoce cambios de detenciones persistidos con un proceso posterior fallido. */
export function isDetentionPartialError(error: any): boolean {
  const code = getApiErrorCode(error);
  return code === 'DETENTION_REBUILD_FAILED' ||
    code === 'DETENTION_REBUILD_FORBIDDEN' ||
    code === 'DETENTION_REPORT_REGENERATION_FAILED' ||
    isProductionRebuildPartialError(error);
}

export function getApiErrorMessage(error: any, fallback: string = 'No fue posible completar la operación.'): string {
  const body = getResponseBody(error);
  const detail = body && typeof body.error === 'object' ? body.error : {};
  const errorCode = getApiErrorCode(error);
  let message = errorCode ? API_ERROR_MESSAGES[errorCode] : null;

  if (!message) {
    const httpCode = error && error.status || detail.code || body.code;
    const remoteStatus = body.remote_status || detail.remote_status;

    if (Number(httpCode) === 403) {
      message = 'No tienes permisos para realizar esta operación.';
    } else if (Number(httpCode) === 502 && Number(remoteStatus) === 403) {
      message = 'La producción fue guardada.';
    } else {
      message = detail.message || body.message ||
        (typeof body.error === 'string' ? body.error : null) || fallback;
    }
  }

  // error_code y request_id son datos de trazabilidad técnica. Los handlers
  // conservan el error completo y la causa posterior al guardado en consola;
  // la interfaz solo muestra el
  // mensaje funcional para no exponer identificadores internos al usuario.
  return message;
}
